import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  PersonOff as NoShowIcon,
  Close as CloseIcon
} from '@mui/icons-material';
// Importar tu servicio
import { appointmentServiceInstance } from '../services/appointmentTimelineService';

const NoShowComponent = ({ 
  appointment, 
  onStatusChange, 
  size = 'medium',
  disabled = false,
  title = "Mark as No Show",
  refreshAfterUpdate = true // Nueva prop para controlar el refresh
}) => {
  // Estados del componente
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  // Determinar si el botón debe estar visible
  const shouldShowButton = () => {
    if (!appointment || !appointment.status) return false;
    
    const status = appointment.status.toLowerCase();
    // Solo mostrar para citas que no han sido completadas, canceladas o ya marcadas como no-show
    const excludedStatuses = ['completed', 'cancelled', 'no show', 'no-show'];
    return !excludedStatuses.includes(status);
  };

  // Abrir modal de confirmación
  const handleOpenDialog = () => {
    setOpen(true);
    setError('');
    setNotes('Patient did not show up for the appointment');
  };

  // Cerrar modal
  const handleCloseDialog = () => {
    if (!loading) {
      setOpen(false);
      setNotes('');
      setError('');
    }
  };

  // Procesar el marcado como No Show
  const handleMarkAsNoShow = async () => {
    if (!appointment || (!appointment.id && !appointment._id)) {
      setError('Invalid appointment data');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Usar el ID correcto (puede ser 'id' o '_id' dependiendo de tu estructura)
      const appointmentId = appointment.id || appointment._id;
      
      console.log('🚫 Marking appointment as No Show:', {
        id: appointmentId,
        patientName: appointment.patientName,
        notes: notes
      });

      // Llamar a tu servicio existente
      const result = await appointmentServiceInstance.markAsNoShow(appointmentId, {
        notes: notes.trim() || 'Patient did not show up for the appointment',
        timestamp: new Date().toISOString()
      });

      console.log('✅ Appointment marked as No Show successfully:', result);

      // Crear el objeto actualizado completo para mantener consistencia con PlayButton
      const updatedAppointment = {
        ...appointment,
        id: appointmentId,
        status: 'No Show',
        notes: notes.trim() || 'Patient did not show up for the appointment',
        updatedAt: new Date().toISOString(),
        // Incluir cualquier dato adicional que regrese el servicio
        ...(result.data || result)
      };

      // Notificar al componente padre con el objeto completo (mismo patrón que PlayButton)
      if (onStatusChange) {
        onStatusChange(updatedAppointment);
      }

      // Cerrar el modal
      handleCloseDialog();

      // Refrescar la página después de un breve delay para permitir que se complete la operación
      if (refreshAfterUpdate) {
        setTimeout(() => {
          window.location.reload();
        }, 500); // Delay de 500ms para una mejor UX
      }

    } catch (error) {
      console.error('❌ Error marking appointment as No Show:', error);
      setError(error.message || 'Failed to mark appointment as No Show. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // No renderizar el botón si no debe mostrarse
  if (!shouldShowButton()) {
    return null;
  }

  return (
    <>
      {/* Botón No Show */}
      <Tooltip title={title} arrow>
        <span>
          <IconButton
            onClick={handleOpenDialog}
            disabled={disabled || loading}
            size={size}
            sx={{
              color: '#d32f2f', // Rojo para indicar acción negativa
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.1)',
                color: '#b71c1c'
              },
              '&:disabled': {
                color: 'rgba(0, 0, 0, 0.26)'
              }
            }}
          >
            <NoShowIcon fontSize={size === 'small' ? 'small' : 'medium'} />
          </IconButton>
        </span>
      </Tooltip>

      {/* Modal de confirmación */}
      <Dialog 
        open={open} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NoShowIcon sx={{ color: '#d32f2f' }} />
            <Typography variant="h6" component="span">
              Mark as No Show
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseDialog} 
            disabled={loading}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {/* Mostrar error si existe */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Información del paciente */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Patient Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Name:</strong> {appointment.patientName || appointment.patient?.name || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Appointment ID:</strong> {appointment.appointmentId || appointment.appointmentNumber || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Date:</strong> {appointment.date ? new Date(appointment.date).toLocaleDateString() : 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Time:</strong> {appointment.startTime && appointment.endTime ? 
                `${appointment.startTime} - ${appointment.endTime}` : 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Current Status:</strong> {appointment.status || 'N/A'}
            </Typography>
          </Box>

          {/* Advertencia */}
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> This action will mark the appointment as "No Show" 
              and cannot be easily undone. Please confirm that the patient did not attend 
              their scheduled appointment.
              {refreshAfterUpdate && (
                <span> The page will refresh automatically after the update.</span>
              )}
            </Typography>
          </Alert>

          {/* Campo de notas */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (Optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional notes about why the patient was marked as No Show..."
            disabled={loading}
            sx={{ mb: 2 }}
          />

          {/* Información adicional */}
          <Typography variant="caption" color="text.secondary">
            This action will update the appointment status to "No Show" and record the timestamp.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ 
          borderTop: '1px solid #e0e0e0', 
          pt: 2, 
          px: 3, 
          pb: 2 
        }}>
          <Button 
            onClick={handleCloseDialog} 
            disabled={loading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleMarkAsNoShow}
            disabled={loading}
            variant="contained"
            color="error"
            startIcon={loading ? <CircularProgress size={16} /> : <NoShowIcon />}
            sx={{
              minWidth: 140,
              '&:disabled': {
                backgroundColor: 'rgba(211, 47, 47, 0.12)'
              }
            }}
          >
            {loading ? 'Processing...' : 'Mark as No Show'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NoShowComponent;